import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, FileText, Loader2, Trash2 } from "lucide-react";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { isFileDocument } from "@/lib/validation";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { ZoomableImage } from "@/components/ui/ZoomableImage";
import { deleteOrderDocument, resolveOrderS3Context } from "@/lib/order-doc-delete";

export type ChecklistDocKind =
  | "bol"
  | "tender"
  | "trailer-photo"
  | "trailer-photo-yard"
  | "trailer-photo-yard-left"
  | "trailer-photo-yard-right"
  | "license-plate-photo";

type KindConfig = {
  title: string;
  short: string;
  table:
    | "order_bol_images"
    | "order_tender_images"
    | "order_trailer_photos"
    | "trailer_event_photos";
  bucket: string;
  queryKey: string;
  countKey: string;
  /** Column on the target table used to scope the query. */
  scopeColumn: "order_id" | "trailer_id";
  /** Extra filter (e.g. photo_type) applied after the scope filter. */
  extraFilter?: { column: string; value: string };
  /**
   * Optional case-insensitive substring match against `image_path`. Used to
   * distinguish trailer photo variants (e.g. left vs right) that share the
   * same `photo_type` but have different storage-path prefixes.
   */
  pathContains?: string;
};

const KIND_CFG: Record<ChecklistDocKind, KindConfig> = {
  bol: {
    title: "Bill of Lading",
    short: "BoL",
    table: "order_bol_images",
    bucket: "bol-images",
    queryKey: "bol-images",
    countKey: "bol-images-count",
    scopeColumn: "order_id",
  },
  tender: {
    title: "Tender Documents",
    short: "Tender",
    table: "order_tender_images",
    bucket: "tender-images",
    queryKey: "tender-images",
    countKey: "tender-images-count",
    scopeColumn: "order_id",
  },
  "trailer-photo": {
    title: "Back of Trailer Photos",
    short: "Trailer photo",
    table: "order_trailer_photos",
    bucket: "trailer-images",
    queryKey: "trailer-photos",
    countKey: "trailer-photos-count",
    scopeColumn: "order_id",
  },
  "trailer-photo-yard": {
    title: "Trailer Photos",
    short: "Trailer photo",
    table: "trailer_event_photos",
    bucket: "trailer-images",
    queryKey: "trailer-event-photos-list",
    countKey: "trailer-event-photos-count",
    scopeColumn: "trailer_id",
    extraFilter: { column: "photo_type", value: "trailer" },
  },
  "trailer-photo-yard-left": {
    title: "Trailer Photos (Left side)",
    short: "Left side trailer photo",
    table: "trailer_event_photos",
    bucket: "trailer-images",
    queryKey: "trailer-event-photos-list",
    countKey: "trailer-event-photos-count",
    scopeColumn: "trailer_id",
    extraFilter: { column: "photo_type", value: "trailer" },
    pathContains: "trailer-left-",
  },
  "trailer-photo-yard-right": {
    title: "Trailer Photos (Right side)",
    short: "Right side trailer photo",
    table: "trailer_event_photos",
    bucket: "trailer-images",
    queryKey: "trailer-event-photos-list",
    countKey: "trailer-event-photos-count",
    scopeColumn: "trailer_id",
    extraFilter: { column: "photo_type", value: "trailer" },
    pathContains: "trailer-right-",
  },
  "license-plate-photo": {
    title: "License Plate Photos",
    short: "License plate photo",
    table: "trailer_event_photos",
    bucket: "trailer-images",
    queryKey: "trailer-event-photos-list",
    countKey: "trailer-event-photos-count",
    scopeColumn: "trailer_id",
    extraFilter: { column: "photo_type", value: "license_plate" },
  },
};

async function getSignedUrl(bucket: string, path: string): Promise<string | null> {
  if (!path) return null;
  if (path.startsWith("http")) return path;
  const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, 3600);
  if (error) {
    console.error("Signed URL failed", error);
    return null;
  }
  return data.signedUrl;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Scope value for the query (order id OR trailer db id depending on kind). */
  scopeId: string | null | undefined;
  kind: ChecklistDocKind;
}

export function ChecklistDocViewer({ open, onOpenChange, scopeId, kind }: Props) {
  const cfg = KIND_CFG[kind];
  const [currentIndex, setCurrentIndex] = useState(0);
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const queryClient = useQueryClient();

  const { data: images, isLoading } = useQuery({
    queryKey: [cfg.queryKey, scopeId, cfg.extraFilter?.value, cfg.pathContains],
    queryFn: async () => {
      if (!scopeId) return [] as Array<{ id: string; image_url: string | null; image_path: string | null; created_at: string }>;
      const selectCols = cfg.table === "trailer_event_photos"
        ? "id, image_path, created_at"
        : "id, image_url, image_path, created_at";
      let q = supabase
        .from(cfg.table as never)
        .select(selectCols)
        .eq(cfg.scopeColumn, scopeId);
      if (cfg.extraFilter) {
        q = q.eq(cfg.extraFilter.column, cfg.extraFilter.value);
      }
      if (cfg.pathContains) {
        q = q.ilike("image_path", `%${cfg.pathContains}%`);
      }
      const { data, error } = await q.order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as Array<{ id: string; image_url?: string | null; image_path: string | null; created_at: string }>;
    },
    enabled: open && !!scopeId,
  });

  useEffect(() => {
    if (!images || images.length === 0) return;
    (async () => {
      const urls: Record<string, string> = {};
      for (const img of images) {
        const path = img.image_path || img.image_url || "";
        const url = await getSignedUrl(cfg.bucket, path);
        if (url) urls[img.id] = url;
      }
      setSignedUrls(urls);
    })();
  }, [images, cfg.bucket]);

  useEffect(() => {
    if (images && currentIndex >= images.length && images.length > 0) {
      setCurrentIndex(images.length - 1);
    }
  }, [images, currentIndex]);

  const handleDelete = async () => {
    const img = images?.[currentIndex];
    if (!img) return;
    const path = img.image_path || img.image_url || "";
    setIsDeleting(true);
    try {
      if (cfg.table === "order_bol_images" || cfg.table === "order_tender_images") {
        // Order-attached documents are mirrored to the NetSuite-facing S3
        // bucket — delete there first (Sync-then-Persist) so nothing is
        // orphaned in the bucket.
        const ctx = await resolveOrderS3Context(scopeId);
        await deleteOrderDocument({
          table: cfg.table,
          bucket: cfg.bucket,
          rowId: img.id,
          imagePath: path,
          orderUniqueId: ctx.orderUniqueId,
          tenantId: ctx.tenantId,
          tenantSlug: ctx.tenantSlug,
        });
      } else {
        if (path && !path.startsWith("http")) {
          await supabase.storage.from(cfg.bucket).remove([path]);
        }
        const { error } = await supabase.from(cfg.table as never).delete().eq("id", img.id);
        if (error) throw error;
      }
      toast.success(`${cfg.short} deleted`);
      queryClient.invalidateQueries({ queryKey: [cfg.queryKey, scopeId, cfg.extraFilter?.value, cfg.pathContains] });
      queryClient.invalidateQueries({ queryKey: [cfg.countKey, scopeId, cfg.extraFilter?.value, cfg.pathContains] });
      // Also invalidate the aggregate/count keys used by ReadinessChecklist
      // (which key on the variant string rather than pathContains).
      queryClient.invalidateQueries({ queryKey: [cfg.countKey, scopeId] });
    } catch (err: any) {
      console.error(err);
      toast.error(`Failed to delete ${cfg.short}: ${err?.message ?? "Unknown error"}`);
    } finally {
      setIsDeleting(false);
      setConfirmingDelete(false);
    }
  };

  useEffect(() => {
    setConfirmingDelete(false);
  }, [currentIndex, open]);

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className="max-h-[90vh]">
          <DrawerHeader>
            <DrawerTitle>{cfg.title}</DrawerTitle>
          </DrawerHeader>
          <div className="px-4 pb-6 overflow-auto">
            {isLoading || !images ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : images.length === 0 ? (
              <p className="text-center text-muted-foreground py-12">No uploads yet.</p>
            ) : (
              <div className="space-y-4">
                <div className="relative bg-muted/30 rounded-lg overflow-hidden border">
                  {(() => {
                    const img = images[currentIndex];
                    const url = img && signedUrls[img.id];
                    const filePath = img?.image_path || img?.image_url || "";
                    if (!url) {
                      return (
                        <div className="flex items-center justify-center py-12">
                          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                        </div>
                      );
                    }
                    if (filePath.toLowerCase().endsWith(".pdf")) {
                      return <iframe src={url} title="Document" className="w-full h-[55vh] border-0" />;
                    }
                    if (isFileDocument(filePath)) {
                      return (
                        <div className="flex flex-col items-center justify-center py-12 gap-3">
                          <FileText className="h-16 w-16 text-muted-foreground" />
                          <Button asChild variant="outline" size="sm">
                            <a href={url} target="_blank" rel="noopener noreferrer">
                              Download to view
                            </a>
                          </Button>
                        </div>
                      );
                    }
                    return (
                      <ZoomableImage
                        src={url}
                        openUrl={url}
                        fileName={filePath.split("/").pop() || cfg.short}
                        alt={cfg.short}
                        className="h-[55vh] w-full"
                      />
                    );
                  })()}
                  {images.length > 1 && (
                    <>
                      <Button
                        variant="secondary"
                        size="icon"
                        className="absolute left-2 top-1/2 -translate-y-1/2 opacity-80"
                        onClick={() => setCurrentIndex((p) => (p > 0 ? p - 1 : images.length - 1))}
                      >
                        <ChevronLeft className="h-5 w-5" />
                      </Button>
                      <Button
                        variant="secondary"
                        size="icon"
                        className="absolute right-2 top-1/2 -translate-y-1/2 opacity-80"
                        onClick={() => setCurrentIndex((p) => (p < images.length - 1 ? p + 1 : 0))}
                      >
                        <ChevronRight className="h-5 w-5" />
                      </Button>
                    </>
                  )}
                  {confirmingDelete && (
                    <div className="absolute inset-0 bg-background/85 backdrop-blur-sm flex flex-col items-center justify-center gap-4 p-6 text-center">
                      <div className="space-y-1">
                        <p className="font-semibold text-foreground">
                          Delete this {kind === "bol" ? "Bill of Lading" : cfg.short.toLowerCase()}?
                        </p>
                        <p className="text-sm text-muted-foreground">
                          This will permanently remove the file. This action cannot be undone.
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={isDeleting}
                          onClick={() => setConfirmingDelete(false)}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          disabled={isDeleting}
                          onClick={handleDelete}
                        >
                          {isDeleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                          Delete
                        </Button>
                      </div>
                    </div>
                  )}
                  {!confirmingDelete && (
                    <Button
                      variant="destructive"
                      size="icon"
                      aria-label="Delete"
                      className="absolute bottom-2 right-2 h-9 w-9 shadow-md"
                      onClick={() => setConfirmingDelete(true)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                {images.length > 1 && (
                  <div className="flex gap-2 justify-center flex-wrap">
                    {images.map((img, i) => (
                      <button
                        key={img.id}
                        onClick={() => setCurrentIndex(i)}
                        className={cn(
                          "w-14 h-14 rounded-md overflow-hidden border-2 transition-all",
                          i === currentIndex
                            ? "border-primary"
                            : "border-transparent opacity-60",
                        )}
                      >
                        {signedUrls[img.id] ? (
                          <img src={signedUrls[img.id]} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full bg-muted" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
                <div className="flex items-center justify-between px-2">
                  <p className="text-sm text-muted-foreground">
                    {currentIndex + 1} of {images.length}
                  </p>
                </div>
              </div>
            )}
          </div>
        </DrawerContent>
    </Drawer>
  );
}